import { produce } from 'immer';
import type {
  AvgSpendBySegmentResponse,
  AcquisitionSourceResponse,
  CustomerDetailsResponse,
  DrilldownCustomersResponse,
  DrilldownStatsResponse,
  ChannelPerformanceResponse,
  CohortRetentionResponse,
  CustomerDirectoryResponse,
  CustomerSegmentBreakdownResponse,
  CustomerStampKpisResponse,
} from '../../../interface/customersReportInterface';
import type { CustomersReportAction } from './customersReportActions';
import {
  FETCH_CUSTOMER_STAMP_KPIS_REQUEST,
  FETCH_CUSTOMER_STAMP_KPIS_SUCCESS,
  FETCH_CUSTOMER_STAMP_KPIS_FAILURE,
  FETCH_CUSTOMER_SEGMENTS_REQUEST,
  FETCH_CUSTOMER_SEGMENTS_SUCCESS,
  FETCH_CUSTOMER_SEGMENTS_FAILURE,
  FETCH_CUSTOMER_DIRECTORY_REQUEST,
  FETCH_CUSTOMER_DIRECTORY_SUCCESS,
  FETCH_CUSTOMER_DIRECTORY_FAILURE,
  FETCH_CHANNEL_PERFORMANCE_REQUEST,
  FETCH_CHANNEL_PERFORMANCE_SUCCESS,
  FETCH_CHANNEL_PERFORMANCE_FAILURE,
  FETCH_COHORT_RETENTION_REQUEST,
  FETCH_COHORT_RETENTION_SUCCESS,
  FETCH_COHORT_RETENTION_FAILURE,
  FETCH_SEGMENT_SPEND_REQUEST,
  FETCH_SEGMENT_SPEND_SUCCESS,
  FETCH_SEGMENT_SPEND_FAILURE,
  FETCH_DRILLDOWN_STATS_REQUEST,
  FETCH_DRILLDOWN_STATS_SUCCESS,
  FETCH_DRILLDOWN_STATS_FAILURE,
  FETCH_DRILLDOWN_CUSTOMERS_REQUEST,
  FETCH_DRILLDOWN_CUSTOMERS_SUCCESS,
  FETCH_DRILLDOWN_CUSTOMERS_FAILURE,
  FETCH_ACQUISITION_SOURCE_REQUEST,
  FETCH_ACQUISITION_SOURCE_SUCCESS,
  FETCH_ACQUISITION_SOURCE_FAILURE,
  FETCH_CUSTOMER_DETAILS_REQUEST,
  FETCH_CUSTOMER_DETAILS_SUCCESS,
  FETCH_CUSTOMER_DETAILS_FAILURE,
  CLEAR_CUSTOMER_DETAILS,
} from './customersReportConstants';

export interface CustomersReportState {
  stampKpisLoading: boolean;
  stampKpis: CustomerStampKpisResponse | null;
  stampKpisError: string | null;
  segmentsLoading: boolean;
  segments: CustomerSegmentBreakdownResponse | null;
  segmentsError: string | null;
  directoryLoading: boolean;
  directory: CustomerDirectoryResponse | null;
  directoryError: string | null;
  channelsLoading: boolean;
  channels: ChannelPerformanceResponse | null;
  channelsError: string | null;
  cohortsLoading: boolean;
  cohorts: CohortRetentionResponse | null;
  cohortsError: string | null;
  segmentSpendLoading: boolean;
  segmentSpend: AvgSpendBySegmentResponse | null;
  segmentSpendError: string | null;
  drilldownStatsLoading: boolean;
  drilldownStats: DrilldownStatsResponse | null;
  drilldownStatsError: string | null;
  drilldownCustomersLoading: boolean;
  drilldownCustomers: DrilldownCustomersResponse | null;
  drilldownCustomersError: string | null;
  detailsLoading: boolean;
  details: CustomerDetailsResponse | null;
  detailsError: string | null;
  acquisitionLoading: boolean;
  acquisition: AcquisitionSourceResponse | null;
  acquisitionError: string | null;
}

const initialState: CustomersReportState = {
  stampKpisLoading: false,
  stampKpis: null,
  stampKpisError: null,
  segmentsLoading: false,
  segments: null,
  segmentsError: null,
  directoryLoading: false,
  directory: null,
  directoryError: null,
  channelsLoading: false,
  channels: null,
  channelsError: null,
  cohortsLoading: false,
  cohorts: null,
  cohortsError: null,
  segmentSpendLoading: false,
  segmentSpend: null,
  segmentSpendError: null,
  drilldownStatsLoading: false,
  drilldownStats: null,
  drilldownStatsError: null,
  drilldownCustomersLoading: false,
  drilldownCustomers: null,
  drilldownCustomersError: null,
  detailsLoading: false,
  details: null,
  detailsError: null,
  acquisitionLoading: false,
  acquisition: null,
  acquisitionError: null,
};

const customersReportReducer = (state = initialState, action: CustomersReportAction): CustomersReportState =>
  produce(state, (draft) => {
    switch (action.type) {
      case FETCH_CUSTOMER_STAMP_KPIS_REQUEST:
        draft.stampKpisLoading = true;
        draft.stampKpisError = null;
        break;
      case FETCH_CUSTOMER_STAMP_KPIS_SUCCESS:
        draft.stampKpisLoading = false;
        draft.stampKpis = action.payload as CustomerStampKpisResponse;
        break;
      case FETCH_CUSTOMER_STAMP_KPIS_FAILURE:
        draft.stampKpisLoading = false;
        // A failed period must not keep showing the previous period's numbers.
        draft.stampKpis = null;
        draft.stampKpisError = action.payload as string;
        break;
      case FETCH_CUSTOMER_SEGMENTS_REQUEST:
        draft.segmentsLoading = true;
        draft.segmentsError = null;
        break;
      case FETCH_CUSTOMER_SEGMENTS_SUCCESS:
        draft.segmentsLoading = false;
        draft.segments = action.payload as CustomerSegmentBreakdownResponse;
        break;
      case FETCH_CUSTOMER_SEGMENTS_FAILURE:
        draft.segmentsLoading = false;
        // Don't leave another outlet's segments on screen after a failure.
        draft.segments = null;
        draft.segmentsError = action.payload as string;
        break;
      case FETCH_CUSTOMER_DIRECTORY_REQUEST:
        draft.directoryLoading = true;
        draft.directoryError = null;
        // Cleared, like the drill slices: the table shows shimmer while the next
        // page loads instead of dimming the previous one, which after a date or
        // outlet change was another period's customers.
        draft.directory = null;
        break;
      case FETCH_CUSTOMER_DIRECTORY_SUCCESS:
        draft.directoryLoading = false;
        draft.directory = action.payload as CustomerDirectoryResponse;
        break;
      case FETCH_CUSTOMER_DIRECTORY_FAILURE:
        draft.directoryLoading = false;
        // Don't leave another outlet's or period's customers on screen.
        draft.directory = null;
        draft.directoryError = action.payload as string;
        break;
      case FETCH_CHANNEL_PERFORMANCE_REQUEST:
        draft.channelsLoading = true;
        draft.channelsError = null;
        break;
      case FETCH_CHANNEL_PERFORMANCE_SUCCESS:
        draft.channelsLoading = false;
        draft.channels = action.payload as ChannelPerformanceResponse;
        break;
      case FETCH_CHANNEL_PERFORMANCE_FAILURE:
        draft.channelsLoading = false;
        // A failed period must not keep showing the previous period's mix.
        draft.channels = null;
        draft.channelsError = action.payload as string;
        break;
      case FETCH_COHORT_RETENTION_REQUEST:
        draft.cohortsLoading = true;
        draft.cohortsError = null;
        break;
      case FETCH_COHORT_RETENTION_SUCCESS:
        draft.cohortsLoading = false;
        draft.cohorts = action.payload as CohortRetentionResponse;
        break;
      case FETCH_COHORT_RETENTION_FAILURE:
        draft.cohortsLoading = false;
        // A failed window must not keep showing another period's grid.
        draft.cohorts = null;
        draft.cohortsError = action.payload as string;
        break;
      case FETCH_SEGMENT_SPEND_REQUEST:
        draft.segmentSpendLoading = true;
        draft.segmentSpendError = null;
        break;
      case FETCH_SEGMENT_SPEND_SUCCESS:
        draft.segmentSpendLoading = false;
        draft.segmentSpend = action.payload as AvgSpendBySegmentResponse;
        break;
      case FETCH_SEGMENT_SPEND_FAILURE:
        draft.segmentSpendLoading = false;
        // A failed period must not keep showing the previous one's averages.
        draft.segmentSpend = null;
        draft.segmentSpendError = action.payload as string;
        break;
      case FETCH_DRILLDOWN_STATS_REQUEST:
        draft.drilldownStatsLoading = true;
        draft.drilldownStatsError = null;
        // Cleared on request, like the directory: opening a different
        // drill must never show the previous drill's header for a frame.
        draft.drilldownStats = null;
        break;
      case FETCH_DRILLDOWN_STATS_SUCCESS:
        draft.drilldownStatsLoading = false;
        draft.drilldownStats = action.payload as DrilldownStatsResponse;
        break;
      case FETCH_DRILLDOWN_STATS_FAILURE:
        draft.drilldownStatsLoading = false;
        draft.drilldownStats = null;
        draft.drilldownStatsError = action.payload as string;
        break;
      case FETCH_DRILLDOWN_CUSTOMERS_REQUEST:
        draft.drilldownCustomersLoading = true;
        draft.drilldownCustomersError = null;
        // Cleared like the header and the directory: the slice is shared by
        // every drill, so a kept page may belong to a different one (QA #15).
        draft.drilldownCustomers = null;
        break;
      case FETCH_DRILLDOWN_CUSTOMERS_SUCCESS:
        draft.drilldownCustomersLoading = false;
        draft.drilldownCustomers = action.payload as DrilldownCustomersResponse;
        break;
      case FETCH_DRILLDOWN_CUSTOMERS_FAILURE:
        draft.drilldownCustomersLoading = false;
        draft.drilldownCustomers = null;
        draft.drilldownCustomersError = action.payload as string;
        break;
      case FETCH_CUSTOMER_DETAILS_REQUEST:
        draft.detailsLoading = true;
        draft.detailsError = null;
        break;
      case FETCH_CUSTOMER_DETAILS_SUCCESS:
        draft.detailsLoading = false;
        draft.details = action.payload as CustomerDetailsResponse;
        break;
      case FETCH_CUSTOMER_DETAILS_FAILURE:
        draft.detailsLoading = false;
        // Clear rather than keep: a 404 for THIS customer must not leave
        // the previous customer's profile on screen under a new name.
        draft.details = null;
        draft.detailsError = action.payload as string;
        break;
      case CLEAR_CUSTOMER_DETAILS:
        draft.detailsLoading = false;
        draft.details = null;
        draft.detailsError = null;
        break;
      case FETCH_ACQUISITION_SOURCE_REQUEST:
        draft.acquisitionLoading = true;
        draft.acquisitionError = null;
        break;
      case FETCH_ACQUISITION_SOURCE_SUCCESS:
        draft.acquisitionLoading = false;
        draft.acquisition = action.payload as AcquisitionSourceResponse;
        break;
      case FETCH_ACQUISITION_SOURCE_FAILURE:
        draft.acquisitionLoading = false;
        // A failed period must not keep showing the previous period's mix.
        draft.acquisition = null;
        draft.acquisitionError = action.payload as string;
        break;
      default:
        break;
    }
  });

export default customersReportReducer;
